import FieldBlock from "./model/FieldBlock";
import { isDateObject } from './dateutil';
import UAParser from 'user-agent-parser';

export * from './error';
export * from './dateutil';

const resetItemsDisplayOrder = (items) => {
  let displayOrder = 1;
  items.forEach(item => {
    item.display_order = displayOrder;
    displayOrder++;
    if (item instanceof FieldBlock) {
      // We also need to increment the field block's field display orders before continuing with the rest of the form
      // (or, if this is a page, this will never trigger)
      // This is to keep order conflicts from happening in the recap response between fields inside and outside the field block
      item.items.forEach(field => {
        item.display_order = displayOrder;
        displayOrder++;
      });
    }
  });
};

let mapObjectsByKey = (objects, oKey) => {
  let key = oKey || 'id';
  let map = objects.reduce((prev, current, idx) => {
    prev[current[key]] = current;
    return prev;
  }, {});
  return map;
};

let sortItems = function(items) {
  let sorted = items.sort((a, b) => {
    let displayOrderA = parseInt(a.display_order),
      displayOrderB = parseInt(b.display_order);
    if (displayOrderA < displayOrderB) {
      return -1
    } else if (displayOrderA > displayOrderB) {
      return 1;
    } else {
      return 0;
    }
  });
  return sorted;
};

let provisionalIdentifierCounts = {};
const provisionalIdentifierPrefix = 'provisional_';

/**
 * Generate a provisional ID for the given type
 * @param type
 * @returns {string}
 */
let getProvisionalIdentifierForType = (type) => {
  if (provisionalIdentifierCounts[type] === undefined) {
    provisionalIdentifierCounts[type] = 0;
  }
  provisionalIdentifierCounts[type] += 1;
  return provisionalIdentifierPrefix + generateUniqueIntegerWithCount(provisionalIdentifierCounts[type]);
};

/**
 * Generates a quasi-unique integer with:
 * - 16 bits of counter
 * - 15 least significant bits of current time in milliseconds
 * @param cnt
 * @returns {number}
 */
let generateUniqueIntegerWithCount = (cnt = 0) => {
  cnt = parseInt(cnt) || 0;
  const timePart = (new Date()).getTime() % (2 ** 16);
  const cntPart = cnt % (2 ** 15);
  const id = (cntPart << 15) + timePart;
  return id;
};

let isProvisionalIdentifier = (identifier) => {
  return isString(identifier) && identifier.startsWith(provisionalIdentifierPrefix);
};

let normalizeIdentifier = (identifier) => {
  if (isProvisionalIdentifier(identifier)) {
    return identifier;
  } else if (identifier === null) {
    return null;
  } else {
    return parseInt(identifier);
  }
};

// A JSON value (a web rank answer) stays whole, as the old `|`-only split left it.
let selectedOptionTokens = (value) => {
  const text = String(value);
  return text.trim().startsWith('[') ? [text] : text.split(/[|,]/).map(token => token.trim());
};

/**
 * The tokens of a document field value, in order. Only an all-digit token is a document id, because
 * the value can also hold an unresolved photo UUID, which parseInt() misreads. Brackets and quotes
 * are dropped as the server drops them.
 */
let documentFieldTokens = (value) => String(value || '').replace(/[[\]"]/g, '').split(/[|,]/)
  .map(token => token.trim())
  .filter(token => token !== '')
  .map(token => {
    if (isProvisionalIdentifier(token)) {
      return { documentId: token };
    }
    if (/^[0-9]+$/.test(token)) {
      return { documentId: parseInt(token, 10) };
    }
    return { other: token };
  });

let documentFieldIds = (value) => documentFieldTokens(value)
  .filter(token => 'documentId' in token)
  .map(token => token.documentId);

/**
 * The value with its document ids replaced by documentIds. Other tokens keep their places, ids that
 * remain keep theirs, and new ids are appended.
 */
let withDocumentFieldIds = (value, documentIds) => {
  const remaining = documentIds.slice();
  const tokens = [];
  documentFieldTokens(value).forEach(token => {
    if (!('documentId' in token)) {
      tokens.push(token.other);
      return;
    }
    const index = remaining.indexOf(token.documentId);
    if (index !== -1) {
      tokens.push(remaining.splice(index, 1)[0]);
    }
  });
  return tokens.concat(remaining).join('|');
};

let castToBoolean = (val, defaultVal = false) => {
  let ret;
  if (val === true || val === false) {
    ret = val;
  } else if (val === undefined || val === null) {
    ret = defaultVal;
  } else if (val.toString) {
    ret = convertStringToBoolean(val.toString());
  } else {
    ret = defaultVal;
  }
  return ret;
};

let convertStringToBoolean = (s) => {
  let value;
  if (s) {
    // Coerce to string first: a value may arrive as a native boolean, and true.substring would throw.
    let firstChar = String(s).substring(0, 1).toUpperCase();
    let falseValues = ['F', 'N', '0'];
    let trueValues = ['T', 'Y', '1'];
    if (falseValues.indexOf(firstChar) !== -1) {
      value = false;
    } else if (trueValues.indexOf(firstChar) !== -1) {
      value = true;
    } else {
      console.warn("Unable to determine boolean value of string:", s);
      value = false;
    }
  } else {
    value = false;
  }
  return value;
};

let convertPlainTextStringToBasicMarkup = (s) => {
  let out = '';
  let paragraphs = s.split(/(\s*\n\s*){2,}/);
  for (let paragraph of paragraphs) {
    paragraph = paragraph.trim();
    if (!paragraph.length) {
      continue;
    }
    out += "<p>\n";
    let lines = paragraph.split(/(\s*\n\s*)/)
      .map(line => line.trim())
      .filter(line => line.length > 0);
    out += lines.join("<br>\n") + "\n";
    out += "</p>\n";
  }
  return out;
};

// The field editor's "Use HTML" is not stored: it reads a name as markup when it contains a tag
// (RecapFieldController::edit() has_html), and offers markup only on display text. A hyperlink
// field's name is an anchor by construction. Every other name is plain text, even one that looks
// like markup, and must display literally (GH-5558).
const MARKUP_NAME_TYPES = ['FIELD_DISPLAY_TEXT', 'FIELD_HYPERLINK'];
const looksLikeHtml = (s) => /<\s*\/?\s*[a-z][\s\S]*>/i.test(String(s || ''));

// `name` defaults to the field's own; pass a translated name to test that one instead.
let fieldNameIsMarkup = (field, name = field && field.name) => {
  const definedAs = field && field.fieldType && field.fieldType.defined_as;
  return MARKUP_NAME_TYPES.indexOf(definedAs) !== -1 && looksLikeHtml(name);
};

let escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

let indexOfByProp = (items, item, prop) => {
  for (let idx in items) {
    if (!items.hasOwnProperty(idx)) continue;
    let currItem = items[idx];
    if (!currItem || currItem[prop] === undefined) {
      console.warn(`item[${prop}] not set or item missing:`, currItem);
      continue;
    }
    if (currItem[prop] == item[prop]) {
      return idx;
    }
  }
  return -1;
};

const isString = obj => {
  "use strict";
  return (typeof obj === 'string') || (obj instanceof String);
};

const dateTimeStringIncludesTimeZone = (dateTimeString) => {
  const tzPatterns = [
    /.*Z$/,
    /.+[+-]\d{4}$/,
    /.+[+-]\d{2}:\d{2}$/,
  ];
  return tzPatterns.some(pattern => pattern.test(dateTimeString));
};

/**
 * Normalize start/end Date objects; essentially ensure that endDate falls on the same day as startDate, unless
 * the time of endDate is earlier than startDate, in which case endDate falls on the next day.
 *
 * Used to work around the fact that we do not support multi-day events properly
 *
 * @param startDate
 * @param endDate
 */
const normalizeSingleDayStartEndDates = (startDate, endDate) => {
  if (!isDateObject(startDate) || !isDateObject(endDate)) {
    return [startDate, endDate];
  }

  // Create copy of endDate and set date part to match startDate
  let endDateCopy = new Date(endDate.getTime());
  endDateCopy.setYear(startDate.getFullYear());
  endDateCopy.setMonth(startDate.getMonth());
  endDateCopy.setDate(startDate.getDate());

  // Compare startDate and endDate; if endDate is less than startDate, it is earlier, and we need to add a day to it
  if (endDateCopy < startDate) {
    endDateCopy.setDate(endDateCopy.getDate() + 1);
  }

  return [startDate, endDateCopy];
};

/**
 * Takes a string in hh:mm format and returns the hh and mm components separately
 * @param durationString
 * @returns {*}
 */
const parseDurationStringIntoHHMM = durationString => {
  "use strict";
  const result = isString(durationString) && durationString.match(/^((\d*):)?(\d+)/);
  if (result) {
    const hrs = parseInt(result[2]) || 0;
    const mins = parseInt(result[3]);
    return [hrs, mins];
  } else {
    return [0, 0];
  }
};

let formatCurrency = (num, prefix = '$') => {
  return prefix + parseFloat(num).toFixed(2).toString();
};

let friendlyUserAgentString = fullUserAgentString => {
  let parser = new UAParser();
  parser.setUA(fullUserAgentString);
  let result = parser.getResult();

  let uaParts = [];
  uaParts.push([result.browser.name, result.browser.version].join(' '));
  uaParts.push([result.os.name, result.os.version].filter(s => !!s).join(' '));
  uaParts.push([result.device.vendor, result.device.model, result.device.type].filter(s => !!s).join(' '));

  let friendly = uaParts.filter(s => !!s).join(' / ');
  return friendly;
};

/**
 * Compare two versions in approximately SemVer format x.y.z.
 * Also handles pre-release versions in the format x.y.z-alpha, x.y.z-beta.1, x.y.z-beta.2, etc.
 * Return -1 of a is less than b
 * Return 1 if a is greater than b
 * Return 0 if a is equal to b
 * Return undefined if no determination can be made

 * @param a
 * @param b
 */
let compareVersions = (a, b) => {
  if (a === b) return 0;
  let dashPartsA = a.split('-');
  let dashPartsB = b.split('-');
  let versionPartsA = dashPartsA[0].split('.');
  let versionPartsB = dashPartsB[0].split('.');
  while (versionPartsA.length || versionPartsB.length) {
    if (!versionPartsA.length) return -1;
    if (!versionPartsB.length) return 1;
    let versionPartA = Number(versionPartsA.shift());
    let versionPartB = Number(versionPartsB.shift());
    if (versionPartB > versionPartA) return -1;
    if (versionPartA > versionPartB) return 1;
    if (isNaN(versionPartA) && !isNaN(versionPartB)) return 1;
    if (isNaN(versionPartB) && !isNaN(versionPartA)) return -1;
  }
  if (dashPartsA.length > 1 && dashPartsB.length > 1) {
    let dashVersionPartsA = dashPartsA[1].split('.');
    let dashVersionPartsB = dashPartsB[1].split('.');
    while (dashVersionPartsA.length || dashVersionPartsB.length) {
      if (!dashVersionPartsA.length) return -1;
      if (!dashVersionPartsB.length) return 1;
      let dashVersionPartA = dashVersionPartsA.shift();
      let dashVersionPartB = dashVersionPartsB.shift();
      if (isNumeric(dashVersionPartA) && isNumeric(dashVersionPartB)) {
        dashVersionPartA = Number(dashVersionPartA);
        dashVersionPartB = Number(dashVersionPartB);
      }
      if (dashVersionPartB > dashVersionPartA) return -1;
      if (dashVersionPartA > dashVersionPartB) return 1;
    }
  } else {
    if (dashPartsA.length <= 1) return 1;
    if (dashPartsB.length <= 1) return -1;
  }
  return undefined;
};

let monkeyPatchForSingleArgumentConsoleLogs = () => {
  // Cordova + Android; monkey-patch console.log stuff to attempt to stringify everything
  let logMethods = ['log', 'info', 'warn', 'error', 'debug', 'dir', 'trace'];
  let c = console || {};
  let log = c.log;
  let warn = c.warn || c.log;
  logMethods.forEach(methodName => {
    let old = console[methodName];
    c[methodName] = (...args) => {
      let argStrings = [];
      let errIdxs = [];
      for (let argIdx = 0; argIdx < args.length; argIdx++) {
        let argString;
        try {
          argString = JSON.stringify(args[argIdx]);
        } catch (e) {
          errIdxs.push(argIdx);
          argString = '(error calling JSON.stringify)';
        }
        argStrings.push(argString);
      }
      let msg = argStrings.join(', ');
      old(msg);
    };
    log(`Installed wrapper for console.${methodName}`);
  });
};

/**
 * Numeric comparisons and other related utils
 */

function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

function numbersAreEqual(n1, n2, err = 0.0001) {
  return isNumeric(n1) && isNumeric(n2) && Math.abs(parseFloat(n1) - parseFloat(n2)) < err;
}

function numberIsLessThan(n1, n2) {
  if (!isNumeric(n1) || !isNumeric(n2)) return false;
  return parseFloat(n1) < parseFloat(n2);
}

function numberIsGreaterThan(n1, n2) {
  if (!isNumeric(n1) || !isNumeric(n2)) return false;
  return parseFloat(n1) > parseFloat(n2);
}

function isFunction(o) {
  return o instanceof Function;
}

function isArray(o) {
  return Array.isArray(o);
}

/**
 * Generate UUID
 * See: https://stackoverflow.com/a/2117523/72
 * @returns {string}
 */
function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Debounce function
 * See: https://medium.com/@TCAS3/debounce-deep-dive-javascript-es6-e6f8d983b7a1
 * @param fn
 * @param time
 * @returns {function(...[*]=)}
 */
function debounce(fn, time, immediate = false, debugText = null) {
  let timeout;

  if (debugText) {
    console.debug(`debounce setup: time=${time} immediate=${immediate} debugText=${debugText}`);
  }

  return function() {
    const functionCall = () => {
      if (debugText) {
        console.debug(`debounce functionCall: time=${time} immediate=${immediate} debugText=${debugText}`);
      }
      timeout = null;
      fn.apply(this, arguments);
    };

    clearTimeout(timeout);

    if (immediate && !timeout) {
      functionCall();
    } else {
      timeout = setTimeout(functionCall, time);
    }
  };
}

function vSelect() {
  /**
   * Formats a Bootstrap Multiselect option list into a VirtualSelect option list
   */
  function formatOptions(options) {
    const selectedOptions = []
    const newOptions = options.map((option) => {
      // Check if the parent option is selected.
      if (castToBoolean(option.selected)) {
        selectedOptions.push(parseInt(option.value))
      }

      // Check if any of the children options are selected.
      // Remove all undefined values after mapping.
      if (option.children && option.children.length) {
        let childrenOptions = option.children
        const childrenSelectedOptions = childrenOptions.map((childOption) => {
          if (castToBoolean(childOption.selected)) {
            return parseInt(childOption.value)
          }
        }).filter((value) => {
          return value !== undefined
        })

        // Add the new children selected options into the main selected array.
        if (childrenSelectedOptions && childrenSelectedOptions.length) {
          selectedOptions.push(...childrenSelectedOptions)
        }
      }

      return formatOption(option)
    })
    return {
      options: newOptions,
      selected: selectedOptions,
    }
  }

  function formatOption(option) {
    if (option.children && option.children.length) {
      option.options = formatChildren(option.children)
    }
    delete option.children // The Virtual Select key is `options`
    const opt = {
      label: option.label,
      options: option.options,
    }
    if (!!option.value) {
      opt.value = parseInt(option.value) // Currently only accepting numeric values
    }
    return opt
  }

  function formatChildren(options) {
    return options.map((option) => formatOption(option))
  }

  function escapeLabel(label) {
    return label === undefined || label === null ? '' : escapeHtml(label)
  }

  function escapeOptionLabels(options) {
    if (!Array.isArray(options)) {
      return options
    }
    return options.map((given) => {
      const option = given !== null && typeof given === 'object' ? given : { value: given, label: given }
      const escaped = { ...option, label: escapeLabel(option.label) }
      if (escaped.label !== String(option.label ?? '')) {
        // The widget searches the stored label, so the alias keeps the text searchable.
        escaped.alias = [].concat(option.alias ?? [], String(option.label))
      }
      if (option.description !== undefined) {
        escaped.description = escapeLabel(option.description)
      }
      if (Array.isArray(option.options)) {
        escaped.options = escapeOptionLabels(option.options)
      }
      return escaped
    })
  }

  // The library's own statics, which init() installs as each element's setOptions/addOption; calling
  // them rather than the element's current method keeps a second init() from escaping twice.
  function escapeLabelsOf(element) {
    element.setOptions = (options, ...rest) => VirtualSelect.setOptionsMethod.call(element, escapeOptionLabels(options), ...rest)
    element.addOption = (option) => VirtualSelect.addOptionMethod.call(element, escapeOptionLabels([option])[0])
  }

  /**
   * VirtualSelect.init() whose labels are text: the widget writes labels into innerHTML, and its own
   * enableSecureText also escapes values and shows a `"` as `&quot;`, so the labels are escaped here.
   */
  function init(config) {
    const widgetConfig = { ...config, enableSecureText: false }
    if (config.options !== undefined) {
      widgetConfig.options = escapeOptionLabels(config.options)
    }
    const created = VirtualSelect.init(widgetConfig)
    for (const instance of [].concat(created || [])) {
      escapeLabelsOf(instance.$ele)
    }
    return created
  }

  const renderSelects = (
    config = {
      maxWidth: '100%',
    }
  ) => {
    if (VirtualSelect) {
      // Virtual Select doesn't like multiple attributes like `multiple="multiple"`, and wants the simple `multiple` instead
      const multipleSelects = document.querySelectorAll('.virtual-select[multiple="multiple"]')
      for (const select of multipleSelects) {
        select.setAttribute('multiple', '')
      }
      const selects = document.querySelectorAll('.virtual-select');
      for (let i = 0; i < selects.length; i++) {
        const select = selects[i]
        if (!select.setOptions) {
          // Only render if it's not already a VirtualSelect
          const selectId = select.getAttribute('id')
          const selectName = select.getAttribute('name')
          const optionNodes = select.querySelectorAll('option')
          const selectedOptions = []
          const options = Array.from(optionNodes).map((opt) => {
            const strVal = opt.value.toString()
            if (opt.hasAttribute('selected')) {
              selectedOptions.push(strVal)
            }
            return {
              label: opt.innerText,
              value: strVal,
            }
          })

          // Remove all the native <option> elements
          optionNodes.forEach(node => node.remove())

          // Initialize the select and set the options
          init({
            ele: selectId ? `#${selectId}` : select,
            ...config,
          })
          const virtualSelect = selectId
            ? document.querySelector(`#${selectId}`)
            : document.querySelector(`.virtual-select[name="${selectName}"]`)
          virtualSelect.setOptions(options)
          virtualSelect.setValue(selectedOptions)
        }
      }
    } else {
      throw new Error('VirtualSelect is not included on this page.')
    }
  }

  function isVirtualSelect(element) {
    return !!element?.setOptions
  }

  return {
    init,
    renderSelects,
    formatOptions,
    isVirtualSelect,
  };
}

function cacheBustImages(elems = []) {
  const force_elems = []
  if (Array.isArray(elems) && elems.length) {
    force_elems.push(...elems)
  } else {
    const images = document.querySelectorAll('img.nni_cache_bust')
    for (const image of images) {
      if (!image.dataset?.cache_busted) {
        force_elems.push(image)
      }
    }
  }
  for (const elem of force_elems) {
    if (!elem.dataset?.forced) {
      const src = elem.src
      if (src.includes('?')) {
        elem.src = src + '&t=' + new Date().getTime()
      } else {
        elem.src = src + '?t=' + new Date().getTime()
      }
      elem.dataset.forced = true
    }
  }
}

export {
  resetItemsDisplayOrder,
  mapObjectsByKey,
  sortItems,
  provisionalIdentifierPrefix,
  getProvisionalIdentifierForType,
  isString,
  isProvisionalIdentifier,
  normalizeIdentifier,
  selectedOptionTokens,
  documentFieldIds,
  withDocumentFieldIds,
  castToBoolean,
  convertStringToBoolean,
  convertPlainTextStringToBasicMarkup,
  fieldNameIsMarkup,
  looksLikeHtml,
  escapeHtml,
  indexOfByProp,
  parseDurationStringIntoHHMM,
  normalizeSingleDayStartEndDates,
  formatCurrency,
  friendlyUserAgentString,
  compareVersions,
  monkeyPatchForSingleArgumentConsoleLogs,
  isNumeric,
  numbersAreEqual,
  numberIsLessThan,
  numberIsGreaterThan,
  isFunction,
  isArray,
  uuidv4,
  debounce,
  vSelect,
  cacheBustImages,
};
